/**
 * Artistic Slideshow Component
 * Enhanced slideshow with smooth CSS transitions and auto-rotate
 * For Slash Cricket Shopify Theme
 */

class ArtisticSlideshowComponent extends HTMLElement {
  constructor() {
    super();
    
    // Core elements
    this.slider = this.querySelector('[id^="Slider-"]');
    this.slides = Array.from(this.querySelectorAll('.slideshow__slide'));
    this.prevButton = this.querySelector('button[name="previous"]');
    this.nextButton = this.querySelector('button[name="next"]');
    this.autoplayButton = this.querySelector('.slideshow__autoplay');
    this.dotButtons = Array.from(this.querySelectorAll('.slider-counter__link'));
    this.currentPageElement = this.querySelector('.slider-counter--current');
    this.pageTotalElement = this.querySelector('.slider-counter--total');
    
    // State
    this.currentIndex = 0;
    this.isPlaying = false;
    this.autoplayInterval = null;
    this.transitionDirection = 'next';
    
    // Settings from data attributes
    this.autoplay = this.slider?.dataset.autoplay === 'true';
    this.speed = (parseInt(this.slider?.dataset.speed) || 5) * 1000;
    this.transitionType = this.dataset.transition || 'fade-scale';
    
    // Initialize
    if (this.slides.length > 0) {
      this.init();
    }
  }
  
  init() {
    // Set initial CSS custom property for speed
    this.style.setProperty('--slideshow-speed', `${this.speed}ms`);
    
    // Add artistic class based on transition type
    this.classList.add('artistic-slideshow');
    this.classList.add(`artistic-slideshow--${this.transitionType}`);
    
    // Hide default scroll behavior  
    if (this.slider) {
      this.slider.style.overflow = 'hidden';
      this.slider.style.scrollSnapType = 'none';
    }
    
    // Position all slides absolutely (handled by CSS, but ensure structure)
    this.slides.forEach((slide, index) => {
      if (index === 0) {
        slide.classList.add('is-selected');
      }
    });
    
    // Create and add progress bar
    this.createProgressBar();
    
    // Bind events
    this.bindEvents();
    
    // Update counter
    this.updateCounter();
    this.updateDots();
    
    // Start autoplay if enabled
    if (this.autoplay) {
      this.play();
    }
  }
  
  createProgressBar() {
    this.progressContainer = document.createElement('div');
    this.progressContainer.className = 'artistic-slideshow__progress';
    
    this.progressBar = document.createElement('div');
    this.progressBar.className = 'artistic-slideshow__progress-bar';
    
    this.progressContainer.appendChild(this.progressBar);
    this.appendChild(this.progressContainer);
  }
  
  bindEvents() {
    // Navigation buttons
    if (this.prevButton) {
      this.prevButton.addEventListener('click', (e) => {
        e.preventDefault();
        this.goToPrev();
      });
    }
    
    if (this.nextButton) {
      this.nextButton.addEventListener('click', (e) => {
        e.preventDefault();
        this.goToNext();
      });
    }
    
    // Autoplay toggle button
    if (this.autoplayButton) {
      this.autoplayButton.addEventListener('click', () => {
        this.toggleAutoplay();
      });
    }
    
    // Dot navigation
    this.dotButtons.forEach((dot, index) => {
      dot.addEventListener('click', (e) => {
        e.preventDefault();
        this.goToSlide(index);
      });
    });
    
    // Pause on hover/focus
    this.addEventListener('mouseenter', () => this.pauseTemporarily());
    this.addEventListener('mouseleave', () => this.resumeIfPlaying());
    this.addEventListener('focusin', () => this.pauseTemporarily());
    this.addEventListener('focusout', () => this.resumeIfPlaying());
    
    // Touch/Swipe support
    this.initTouchEvents();
    
    // Keyboard navigation
    this.addEventListener('keydown', (e) => {
      if (e.key === 'ArrowLeft') {
        this.goToPrev();
      } else if (e.key === 'ArrowRight') {
        this.goToNext();
      }
    });
    
    // Visibility change - pause when tab is hidden
    document.addEventListener('visibilitychange', () => {
      if (document.hidden) {
        this.pauseTemporarily();
      } else {
        this.resumeIfPlaying();
      }
    });
  }
  
  initTouchEvents() {
    let touchStartX = 0;
    let touchEndX = 0;
    const minSwipeDistance = 50;
    
    this.addEventListener('touchstart', (e) => {
      touchStartX = e.changedTouches[0].screenX;
    }, { passive: true });
    
    this.addEventListener('touchend', (e) => {
      touchEndX = e.changedTouches[0].screenX;
      const swipeDistance = touchStartX - touchEndX;
      
      if (Math.abs(swipeDistance) > minSwipeDistance) {
        if (swipeDistance > 0) {
          this.goToNext();
        } else {
          this.goToPrev();
        }
      }
    }, { passive: true });
  }
  
  goToSlide(index, direction = null) {
    if (index === this.currentIndex || this.isTransitioning) return;
    
    this.isTransitioning = true;
    
    // Determine direction
    if (!direction) {
      direction = index > this.currentIndex ? 'next' : 'prev';
    }
    this.transitionDirection = direction;
    
    const currentSlide = this.slides[this.currentIndex];
    const nextSlide = this.slides[index];
    
    // Apply transition classes based on type
    if (this.transitionType === 'slide-fade') {
      currentSlide.classList.add(`is-leaving-${direction}`);
      nextSlide.classList.add(`is-entering-${direction}`);
    } else {
      currentSlide.classList.add('is-leaving');
      nextSlide.classList.add('is-entering');
    }
    
    // Trigger reflow
    void nextSlide.offsetWidth;
    
    // Remove entering class and add selected
    requestAnimationFrame(() => {
      if (this.transitionType === 'slide-fade') {
        nextSlide.classList.remove(`is-entering-${direction}`);
      } else {
        nextSlide.classList.remove('is-entering');
      }
      nextSlide.classList.add('is-selected');
      currentSlide.classList.remove('is-selected');
      currentSlide.classList.add('is-previous');
    });
    
    // Cleanup after transition
    setTimeout(() => {
      if (this.transitionType === 'slide-fade') {
        currentSlide.classList.remove(`is-leaving-${direction}`);
      } else {
        currentSlide.classList.remove('is-leaving');
      }
      currentSlide.classList.remove('is-previous');
      this.isTransitioning = false;
    }, 1000);
    
    // Update state
    this.currentIndex = index;
    this.updateCounter();
    this.updateDots();
    
    // Restart progress bar
    this.restartProgressBar();
    
    // Dispatch event
    this.dispatchEvent(new CustomEvent('slideChanged', {
      detail: {
        currentPage: this.currentIndex + 1,
        currentElement: nextSlide
      }
    }));
  }
  
  goToNext() {
    const nextIndex = (this.currentIndex + 1) % this.slides.length;
    this.goToSlide(nextIndex, 'next');
  }
  
  goToPrev() {
    const prevIndex = (this.currentIndex - 1 + this.slides.length) % this.slides.length;
    this.goToSlide(prevIndex, 'prev');
  }
  
  updateCounter() {
    if (this.currentPageElement) {
      this.currentPageElement.textContent = this.currentIndex + 1;
    }
    if (this.pageTotalElement) {
      this.pageTotalElement.textContent = this.slides.length;
    }
  }
  
  updateDots() {
    this.dotButtons.forEach((dot, index) => {
      dot.classList.toggle('slider-counter__link--active', index === this.currentIndex);
      dot.setAttribute('aria-current', index === this.currentIndex ? 'true' : 'false');
    });
  }
  
  play() {
    this.isPlaying = true;
    this.classList.remove('is-paused');
    
    if (this.autoplayButton) {
      this.autoplayButton.classList.remove('slideshow__autoplay--paused');
      this.autoplayButton.setAttribute('aria-label', window.accessibilityStrings?.pauseSlideshow || 'Pause slideshow');
    }
    
    this.slider?.setAttribute('aria-live', 'off');
    
    // Start progress bar animation
    this.startProgressBar();
    
    // Clear any existing interval
    this.clearAutoplayInterval();
    
    // Start new interval
    this.autoplayInterval = setInterval(() => {
      this.goToNext();
    }, this.speed);
  }
  
  pause() {
    this.isPlaying = false;
    this.classList.add('is-paused');
    
    if (this.autoplayButton) {
      this.autoplayButton.classList.add('slideshow__autoplay--paused');
      this.autoplayButton.setAttribute('aria-label', window.accessibilityStrings?.playSlideshow || 'Play slideshow');
    }
    
    this.slider?.setAttribute('aria-live', 'polite');
    
    // Stop progress bar
    this.stopProgressBar();
    
    this.clearAutoplayInterval();
  }
  
  toggleAutoplay() {
    if (this.isPlaying) {
      this.pause();
    } else {
      this.play();
    }
  }
  
  pauseTemporarily() {
    if (this.isPlaying) {
      this.clearAutoplayInterval();
      this.pauseProgressBar();
    }
  }
  
  resumeIfPlaying() {
    if (this.isPlaying) {
      this.resumeProgressBar();
      this.clearAutoplayInterval();
      this.autoplayInterval = setInterval(() => {
        this.goToNext();
      }, this.speed);
    }
  }
  
  clearAutoplayInterval() {
    if (this.autoplayInterval) {
      clearInterval(this.autoplayInterval);
      this.autoplayInterval = null;
    }
  }
  
  startProgressBar() {
    if (!this.progressBar) return;
    this.progressBar.style.width = '0%';
    this.progressBar.classList.remove('is-animating');
    void this.progressBar.offsetWidth; // Force reflow
    this.progressBar.classList.add('is-animating');
  }
  
  restartProgressBar() {
    if (!this.progressBar || !this.isPlaying) return;
    this.startProgressBar();
  }
  
  stopProgressBar() {
    if (!this.progressBar) return;
    this.progressBar.classList.remove('is-animating');
    this.progressBar.style.width = '0%';
  }
  
  pauseProgressBar() {
    if (!this.progressBar) return;
    const computedStyle = getComputedStyle(this.progressBar);
    const currentWidth = computedStyle.width;
    this.progressBar.classList.remove('is-animating');
    this.progressBar.style.width = currentWidth;
  }
  
  resumeProgressBar() {
    if (!this.progressBar || !this.isPlaying) return;
    // For simplicity, restart the progress bar
    this.startProgressBar();
  }
  
  // Cleanup
  disconnectedCallback() {
    this.clearAutoplayInterval();
  }
}

// Register the custom element
if (!customElements.get('artistic-slideshow')) {
  customElements.define('artistic-slideshow', ArtisticSlideshowComponent);
}
